import React, { useEffect, useMemo, useState } from 'react';
import { Select, Space, Table, Typography, Empty } from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { selectFilter } from '../utils/selectFilter';

// Campaign Stats Today > Calls & Staff: the day's offered inbound calls (and
// the 6-week day-of-week average) per 15 minutes beside the staffing of the shift departments and jobs picked, per 15
// minutes -- Scheduled, Out (absent), Break / Lunch, Available
// (/api/scheduled-staffing/slots: actual breaks for the time already gone,
// the plan after). Shift departments, not teams: anyone scheduled into the
// department counts. Picking a campaign starts the filters from its defaults
// (Admin > Campaigns: the default team's shift departments and the default
// job); they can be changed.

const STORE = 'staffing-tab-filters';
// Full time for every 15 minutes, e.g. 5:15pm.
const timeLabel = (h, q) => `${h % 12 || 12}:${String(q * 15).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`;
const fmt1 = (v) => (Number.isInteger(v) ? v : Number(v.toFixed(1)));
// Out, Break / Lunch and Available are shares of the 15 minutes: up to 2 places.
const fmt2 = (v) => (Number.isInteger(v) ? v : Number(v.toFixed(2)));

export default function StaffingTab({ date, campaign, offeredByQuarter, handledByQuarter = {}, refreshKey }) {
  const [deptOptions, setDeptOptions] = useState([]);
  const [jobs, setJobs]               = useState([]);
  const [defaults, setDefaults]       = useState([]);
  const [depts, setDepts]             = useState(() => { try { return JSON.parse(localStorage.getItem(STORE))?.depts || []; } catch { return []; } });
  const [jobIds, setJobIds]           = useState(() => { try { return JSON.parse(localStorage.getItem(STORE))?.jobIds || []; } catch { return []; } });
  const [data, setData]               = useState(null);
  // Day-of-week average offered per 15 minutes (the 6 weeks before), by slot index.
  const [avgByQuarter, setAvg]        = useState(null);
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    api.get('/api/jobs').then((r) => setJobs(r.data || [])).catch(() => {});
    api.get('/api/campaign-defaults').then((r) => setDefaults(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!date) return;
    api.get('/api/scheduled-staffing/departments', { params: { start: date, end: date } })
      .then((r) => setDeptOptions((r.data || []).map((n) => ({ value: n, label: n }))))
      .catch(() => setDeptOptions([]));
  }, [date]);

  useEffect(() => {
    if (!date || !campaign) { setAvg(null); return; }
    api.get('/api/campaign-stats-today/lookback-by-quarter', { params: { campaign, date } })
      .then((r) => {
        const m = {};
        (r.data || []).forEach((x) => { m[x.Hour * 4 + x.Quarter] = x.AvgOffered || 0; });
        setAvg(m);
      })
      .catch(() => setAvg(null));
  }, [date, campaign]);

  // The campaign's defaults, when it has them (the first campaign picked).
  const firstCampaign = String(campaign || '').split(',')[0];
  useEffect(() => {
    const d = defaults.find((x) => String(x.CXCampaignNo || '') === firstCampaign);
    if (!d) return;
    if (d.DefaultShiftDepartments?.length) setDepts(d.DefaultShiftDepartments);
    if (d.DefaultJobID) setJobIds([d.DefaultJobID]);
  }, [firstCampaign, defaults]);

  useEffect(() => {
    try { localStorage.setItem(STORE, JSON.stringify({ depts, jobIds })); } catch { /* private window */ }
  }, [depts, jobIds]);

  useEffect(() => {
    if (!date || !depts.length) { setData(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({ date, ...(jobIds.length ? { job_ids: jobIds.join(',') } : {}) });
    depts.forEach((d) => qs.append('department', d));
    api.get(`/api/scheduled-staffing/slots?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, depts, jobIds, refreshKey]);

  // Hours from the first to the last with anyone scheduled or any calls.
  const { hours, slotIdx } = useMemo(() => {
    if (!data) return { hours: [], slotIdx: [] };
    const busy = (h) => [0, 1, 2, 3].some((q) => {
      const i = h * 4 + q;
      return (offeredByQuarter[i] || 0) > 0 || (avgByQuarter?.[i] || 0) > 0 || data.rows[i].sched > 0;
    });
    const hs = Array.from({ length: 24 }, (_, h) => h).filter(busy);
    if (!hs.length) return { hours: [], slotIdx: [] };
    const span = Array.from({ length: hs[hs.length - 1] - hs[0] + 1 }, (_, i) => hs[0] + i);
    return { hours: span, slotIdx: span.flatMap((h) => [0, 1, 2, 3].map((q) => h * 4 + q)) };
  }, [data, offeredByQuarter, avgByQuarter]);

  if (!date) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run a search first." />;

  // Every row is per 15 minutes; val(i) for the call rows, data.rows[i] for staffing.
  const ROWS = [
    { key: 'offered', label: 'Offered', calls: true, val: (i) => offeredByQuarter[i] || 0 },
    ...(avgByQuarter ? [{ key: 'avg', label: 'DOW Avg (6 wks)', calls: true, muted: true, val: (i) => fmt1(avgByQuarter[i] || 0) }] : []),
    { key: 'handled', label: 'Handled', calls: true, val: (i) => handledByQuarter[i] || 0 },
    { key: 'sched', label: 'Scheduled', color: '#595959' },
    { key: 'out', label: 'Out (absent notification)', color: '#a8071a' },
    { key: 'brk', label: 'Break / Lunch', color: '#d46b08' },
    // Actual for the slots loaded from CCaaS, planned after.
    { key: 'offq', label: 'QA Coaching / Non-ACD', color: '#722ed1' },
    { key: 'avail', label: 'Available', color: '#237804', strong: true },
    { key: 'cpa', label: 'Calls per Available', calls: true, val: (i) => {
      const calls = offeredByQuarter[i] || 0;
      const avail = data.rows[i].avail;
      if (!calls) return 0;
      return avail > 0 ? fmt1(calls / avail) : <span style={{ color: '#ff4d4f', fontWeight: 600 }}>no one</span>;
    } },
  ];

  const columns = [
    { title: '', dataIndex: 'label', key: 'label', fixed: 'left', width: 150,
      render: (v, r) => <strong style={{ fontSize: 12 }}>{v}</strong> },
    ...slotIdx.map((i) => {
      const h = Math.floor(i / 4); const q = i % 4;
      return {
        key: `s${i}`, width: 62, align: 'center',
        title: <span style={{ fontSize: 11, fontWeight: q ? 400 : 700, whiteSpace: 'nowrap' }}>{timeLabel(h, q)}</span>,
        onHeaderCell: () => ({ style: { padding: '4px 0', ...(q ? {} : { borderLeft: '2px solid #bfbfbf' }) } }),
        onCell: () => ({
          style: { padding: '3px 0', fontSize: 12, ...(q ? {} : { borderLeft: '2px solid #bfbfbf' }) },
        }),
        render: (_, r) => {
          if (r.calls) {
            const v = r.val(i);
            if (v === 0) return <span style={{ color: '#d9d9d9' }}>·</span>;
            return <span style={{ color: r.muted ? '#8c8c8c' : undefined, fontWeight: r.muted ? 400 : 600 }}>{v}</span>;
          }
          const v = data.rows[i][r.key];
          if (!v && r.key !== 'avail') return <span style={{ color: '#d9d9d9' }}>·</span>;
          return <span style={{ color: r.color, fontWeight: r.strong ? 700 : 500 }}>{fmt2(v)}</span>;
        },
      };
    }),
  ];

  const until = data?.actualUntil;
  const breaksNote = until == null ? '' : until >= 1440 ? 'Breaks: actual'
    : until <= 0 ? 'Breaks: plan'
      : `Breaks: actual through ${dayjs().startOf('day').add(until, 'minute').format('h:mm A')}, plan after`;

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <span>Shift Department:</span>
        <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="Pick a department"
          style={{ minWidth: 240 }} maxTagCount="responsive" value={depts} onChange={setDepts} options={deptOptions} />
        <span>Job:</span>
        <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All jobs"
          style={{ minWidth: 200 }} maxTagCount="responsive" value={jobIds} onChange={setJobIds}
          options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))} />
        {breaksNote && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{breaksNote}</Typography.Text>}
      </Space>
      {!depts.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick a shift department." />
      ) : (
        <Table
          rowKey="key"
          size="small"
          bordered
          loading={loading}
          dataSource={ROWS}
          columns={columns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nobody scheduled and no calls on this day.' }}
          {...(!hours.length && data ? { dataSource: [] } : {})}
        />
      )}
    </div>
  );
}
